<template>
  <div class="mb-10">
    <div class="mb-2 font-weight-bold text-dark font-size-h3">
      <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'panel_' + panelId">
        <i v-if="!isExpanded" class="fa fa-angle-double-down fa-lg" title="Expand"/>
        <i v-if="isExpanded" class="fa fa-angle-double-up fa-lg" title="Collpase"/>
      </b-button>
      {{ title }}
    </div>

    <b-collapse v-model="isExpanded" :id="'panel_' + panelId">
      <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white"
              header-class="font-size-lg font-weight-bold" body-class="p-3">
        <b-alert v-if="deletedInclusions.length > 0" variant="warning" show>
          <div class="text-dark">
            Some {{ title }} have been deleted: <br />
            <div v-for="(deletedInclusion, index) in deletedInclusions" :key="index">
              <li>{{ deletedInclusion.K_Title }}</li>
            </div>
          </div>
        </b-alert>

        <table class="table table-sm table-hover table-head-solid table-bordered">
          <thead>
            <th width="15%" class="text-center">Title</th>
            <th v-if="!ignoreBusinessActivities" width="15%" class="text-center">Business Activity</th>
            <th width="70%" class="text-center">Text</th>
          </thead>
          <tbody>
            <tr v-for="(inclusion, index) in selectedInclusions" :key="index">
              <td>
                <label class="col-form-label font-size-h6"><strong>{{ inclusion.K_Title }}</strong></label>
              </td>
              <td class="text-center" v-if="!ignoreBusinessActivities">
                <label class="col-form-label">{{ inclusion.K_BusinessActivity ? inclusion.K_BusinessActivity.name : ''}}</label>
              </td>
              <td class="pb-3">
                <b-form-textarea class="form-control" v-model="inclusion.K_Text" rows="10"  maxlength="32000" disabled />
              </td>
            </tr>
          </tbody>
        </table>

        <div class="mt-10" v-if="allInclusionsMinusSelected.length > 0">
          <b-card-text class="mb-0">
            <b>To select more inclusions, select one or more options from the
              drop-down list and click 'Add' button.
            </b>
          </b-card-text>

          <div class="row mb-0">
            <div class="form-group col-10">
              <v-select
                v-model="tempArray"
                :items="allInclusionsMinusSelected"
                :item-text="getItemTitle"
                return-object
                multiple
                chips
                deletable-chips
                clearable
                :disabled="isReadOnly"
              ></v-select>
            </div>
            <div class="form-group col-2">
              <button
                class="btn btn-primary btn-block ml-1"
                @click.prevent="addToSelectedList"
                :disabled="isReadOnly"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </b-card>
    </b-collapse>
  </div>
</template>

<style lang="scss">
</style>

<script>
import _ from "lodash";

export default {
  name: "Inclusions",
  props: {
    title: String,
    inclusionKey: String,
    allInclusions: Array,
    selectedInclusions: Array,
    expanded: String,
    ignoreBusinessActivities: Boolean,
    isReadOnly: Boolean,
    showOrder: {
      type: Boolean,
      default: true
    },
    showTextModifiedColumn: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isExpanded: null,
      tempArray: [],
      selectedInclusionsCopy: []
    };
  },
  mounted() {
    this.isExpanded = this.expanded !== "false";
  },

  computed: {
    panelId: function() {
      return this.title
        .replace(/\W/g, "")
        .trim()
        .toLowerCase();
    },

    allInclusionsMinusSelected: function() {
      let filteredInclusions = [];
      if (Array.isArray(this.allInclusions) && this.allInclusions.length > 0) {
        filteredInclusions = _.filter(this.allInclusions, inclusion => {
          return !this.isAlreadySelected(inclusion);
        });
      }
      return filteredInclusions;
    },

    deletedInclusions: function() {
      let nonOptionalDeletedInclusions = [];
      if (
        Array.isArray(this.allInclusionsMinusSelected) &&
        this.allInclusionsMinusSelected.length > 0
      ) {
        nonOptionalDeletedInclusions = _.filter(
          this.allInclusionsMinusSelected,
          inclusion => {
            return !inclusion.K_Optional;
          }
        );
      }
      return nonOptionalDeletedInclusions;
    }
  },

  methods: {
    getItemTitle: function(item) {
      let title = item.K_Title;
      if(!this.ignoreBusinessActivities && item.K_BusinessActivity) {
        title = `${title} (${item.K_BusinessActivity.name})`;
      }
      return title;
    },

    addToSelectedList: function() {
      if (Array.isArray(this.tempArray) && this.tempArray.length > 0) {
        this.selectedInclusionsCopy = this.selectedInclusions || [];

        // Add to selectedInclusionsCopy array if not already present
        this.tempArray.forEach(incl => {
          if (!this.isAlreadySelected(incl)) {
            this.selectedInclusionsCopy.push(incl);
          }
        });

        // Sort by business-activity-name and section-order
        for (let incl of this.selectedInclusionsCopy) {
          if (incl.K_BusinessActivity) {
            incl.BusinessActivityName = incl.K_BusinessActivity.name;
          }
        }

        this.selectedInclusionsCopy = _.orderBy(
          this.selectedInclusionsCopy,
          ["BusinessActivityName", "K_SectionOrder"],
          ["asc", "asc"]
        );

        // Notify the parent component
        this.notifyDataUpdate();
      }
      this.tempArray = [];
    },

    deleteFromSelectedList: function(index) {
      if (Array.isArray(this.selectedInclusions) && this.selectedInclusions.length > 0) {
        this.selectedInclusionsCopy = this.selectedInclusions;
        this.selectedInclusionsCopy.splice(index, 1);

        // Notify the parent component
        this.notifyDataUpdate();
      }
    },

    isAlreadySelected: function(inclusion) {
      if (Array.isArray(this.selectedInclusions) && this.selectedInclusions.length > 0) {
        return _.some(this.selectedInclusions, { Name: inclusion.Name });
      }
      return false;
    },

    notifyDataUpdate: function() {
      this.$emit("onUpdate", this.inclusionKey, this.selectedInclusionsCopy);
    },

    editInclusion: function(index) {
      let toBeEdited = this.selectedInclusions[index];
      this.$emit("onEditInclusion", this.inclusionKey, index, toBeEdited);
    }
  }
};
</script>
